/**
 * API 错误处理增强工具
 *
 * 在请求层 error-handler 的基础上，提供业务层的错误处理包装。
 * 包含 ProTable 请求包装器、统一错误处理器等。
 *
 */

import { ErrorShowType } from '../constants/ui';
import type { ProTableResponse } from '../types/api';

/**
 * API 错误处理配置
 */
export interface ApiErrorHandlerConfig {
  /** message 展示函数 */
  showMessage?: (
    content: string,
    type: 'success' | 'error' | 'warning' | 'info',
  ) => void;
  /** notification 展示函数 */
  showNotification?: (options: {
    message: string;
    description?: string;
  }) => void;
  /** 重定向函数 */
  redirect?: (path: string) => void;
  /** 登录过期回调 */
  onUnauthorized?: () => void;
  /** 登录过期的状态码 */
  unauthorizedCodes?: (string | number)[];
  /** notification 标题，默认 'Request Error' */
  notificationTitle?: string;
  /** 重定向路径，默认 '/error' */
  redirectPath?: string;
}

/**
 * 创建项目级 API 错误处理器
 *
 * @param config - 错误处理配置
 * @returns 错误处理函数
 *
 * @example
 * ```ts
 * const handleApiError = createApiErrorHandler({
 *   showMessage: (content, type) => message[type](content),
 *   showNotification: ({ message: msg, description }) => notification.error({ message: msg, description }),
 *   onUnauthorized: () => emitLoginRequired(),
 *   unauthorizedCodes: [401, 10014],
 *   notificationTitle: '请求错误',
 * });
 *
 * // 在请求拦截器中使用
 * handleApiError(error, ErrorShowType.ERROR_MESSAGE);
 * ```
 */
export function createApiErrorHandler(config: ApiErrorHandlerConfig) {
  const {
    showMessage,
    showNotification,
    redirect,
    onUnauthorized,
    unauthorizedCodes = [401, 10014],
    notificationTitle = 'Request Error',
    redirectPath = '/error',
  } = config;

  return function handleApiError(
    error: unknown,
    showType: ErrorShowType = ErrorShowType.ERROR_MESSAGE,
  ): void {
    const errorMessage = extractErrorMessage(error);
    const errorCode = extractErrorCode(error);

    // 检查登录过期
    if (
      errorCode !== undefined &&
      unauthorizedCodes.some((c) => String(c) === String(errorCode))
    ) {
      onUnauthorized?.();
      return;
    }

    switch (showType) {
      case ErrorShowType.SILENT:
        break;
      case ErrorShowType.WARN_MESSAGE:
        showMessage?.(errorMessage, 'warning');
        break;
      case ErrorShowType.ERROR_MESSAGE:
        showMessage?.(errorMessage, 'error');
        break;
      case ErrorShowType.NOTIFICATION:
        showNotification?.({
          message: notificationTitle,
          description: errorMessage,
        });
        break;
      case ErrorShowType.REDIRECT:
        redirect?.(redirectPath);
        break;
    }
  };
}

/**
 * 从错误对象中提取错误消息
 *
 * @param error - 错误对象
 * @param defaultMessage - 默认错误消息，默认 'Unknown error'
 * @returns 错误消息字符串
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'Unknown error',
): string {
  if (!error) return defaultMessage;

  // Error 实例
  if (error instanceof Error) return error.message;

  // API 响应格式 { msg: '...' } 或 { message: '...' }
  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    if (typeof obj.msg === 'string') return obj.msg;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.errorMessage === 'string') return obj.errorMessage;
  }

  // 字符串
  if (typeof error === 'string') return error;

  return defaultMessage;
}

/**
 * 从错误对象中提取错误码
 *
 * @param error - 错误对象
 * @returns 错误码
 */
export function extractErrorCode(error: unknown): string | number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const obj = error as Record<string, unknown>;
  if (obj.code !== undefined) return obj.code as string | number;
  if (obj.status !== undefined) return obj.status as string | number;
  if (obj.statusCode !== undefined) return obj.statusCode as string | number;
  return undefined;
}

/**
 * 包装 ProTable 的 request 函数，自动处理错误并返回标准格式
 *
 * 确保即使 API 调用失败，ProTable 也不会崩溃，始终返回合法的空数据。
 *
 * @param requestFn - 原始请求函数
 * @param options - 配置选项
 * @returns 包装后的请求函数
 *
 * @example
 * ```ts
 * const safeRequest = wrapProTableRequest(
 *   async (params) => {
 *     const res = await getUserList(params);
 *     return { data: res.data.list, total: res.data.total, success: true };
 *   },
 *   { onError: (err) => message.error(err.message) }
 * );
 *
 * <ProTable request={safeRequest} />
 * ```
 */
export function wrapProTableRequest<T, P = Record<string, unknown>>(
  requestFn: (params: P) => Promise<ProTableResponse<T>>,
  options?: {
    /** 错误回调 */
    onError?: (error: Error) => void;
  },
): (params: P) => Promise<ProTableResponse<T>> {
  return async (params: P): Promise<ProTableResponse<T>> => {
    try {
      return await requestFn(params);
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error(extractErrorMessage(error));
      options?.onError?.(err);
      return { data: [], success: false, total: 0 };
    }
  };
}
